import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  List,
  ListItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCredentialPanelStyles } from "../../../../../theme/panelStyles";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { KauflandApi } from "../../../../../services/api/multi/kaufland_credentials";

const KauflandCredentials = () => {
  const theme = useTheme();
  const cp = getCredentialPanelStyles(theme);

  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formTestStatus, setFormTestStatus] = useState("");
  const [editingCredential, setEditingCredential] = useState(null);

  // Form states
  const [credentialForm, setCredentialForm] = useState({
    display_name: "",
    client_key: "",
    secret_key: "",
  });

  const [activeCredential, setActiveCredential] = useState(() => {
    const saved = localStorage.getItem("activeKauflandCredential");
    return saved ? JSON.parse(saved) : null;
  });

  // Dialog states
  const [credentialDialog, setCredentialDialog] = useState(false);

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const CACHE_KEY = "kaufland_credentials_cache";
      const CACHE_DURATION = 300 * 1000; // 300 seconds

      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION) {
              setCredentials(data);
              // Test all credentials in parallel
              const testPromises = data.map((credential) =>
                testCredentialStatus(credential),
              );
              await Promise.allSettled(testPromises);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error("Error parsing cache", e);
            localStorage.removeItem(CACHE_KEY);
          }
        }
      }

      const response = await KauflandApi.getKauflandCredentials();
      const credentialList = response.data || [];
      setCredentials(credentialList);

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: credentialList,
          timestamp: Date.now(),
        }),
      );

      // Test all credentials in parallel
      const testPromises = credentialList.map((credential) =>
        testCredentialStatus(credential),
      );
      await Promise.allSettled(testPromises);
    } catch (error) {
      console.error("Failed to load credentials:", error);
      setError("Failed to load credentials: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleActiveCredential = async (credential) => {
    if (credential.status !== "Connected") {
      setError("Only connected credentials can be activated");
      return;
    }

    try {
      setLoading(true);

      // Call backend to toggle status (backend ensures only one active)
      const response = await KauflandApi.toggle_credential_status(
        credential.id,
      );

      if (response.data.active) {
        // This credential was activated
        setActiveCredential(credential);
        localStorage.setItem(
          "activeKauflandCredential",
          JSON.stringify(credential),
        );
        setSuccess(`Activated credential: ${credential.display_name}`);

        // Dispatch custom event for other tabs to react
        window.dispatchEvent(new Event("activeKauflandCredentialChanged"));
      } else {
        // This credential was deactivated
        setActiveCredential(null);
        localStorage.removeItem("activeKauflandCredential");
        setSuccess("Credential deactivated");

        // Dispatch custom event for other tabs to react
        window.dispatchEvent(new Event("activeKauflandCredentialChanged"));
      }

      // Reload credentials to update UI for all credentials
      await loadCredentials(true);
    } catch (error) {
      setError("Failed to toggle credential status: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const testCredentialStatus = async (credential) => {
    try {
      const response = await KauflandApi.testKauflandCredentials(credential.id);

      // Update the credential status in the state
      setCredentials((prev) =>
        prev.map((cred) =>
          cred.id === credential.id
            ? {
                ...cred,
                status: response.data.success ? "Connected" : "Disconnected",
              }
            : cred,
        ),
      );
    } catch (error) {
      console.error(`Failed to test credential ${credential.id}:`, error);
      // Update status to disconnected on error
      setCredentials((prev) =>
        prev.map((cred) =>
          cred.id === credential.id
            ? { ...cred, status: "Disconnected" }
            : cred,
        ),
      );
    }
  };

  const handleCredentialSubmit = async () => {
    // For new credentials, all fields are required
    // For editing, display_name is always required, keys are optional (only update if changed)
    if (!credentialForm.display_name) {
      setError("Display name is required");
      return;
    }

    // If creating new credential, keys are required
    if (
      !editingCredential &&
      (!credentialForm.client_key || !credentialForm.secret_key)
    ) {
      setError("Client Key and Secret Key are required for new credentials");
      return;
    }

    // If keys are provided, validate their length
    if (credentialForm.client_key && credentialForm.client_key.length !== 32) {
      setError("Client Key must be exactly 32 characters");
      return;
    }

    if (credentialForm.secret_key && credentialForm.secret_key.length !== 64) {
      setError("Secret Key must be exactly 64 characters");
      return;
    }

    try {
      setLoading(true);

      if (editingCredential) {
        // Update existing credential - only send fields that are provided
        const updateData = {
          display_name: credentialForm.display_name,
        };

        // Only include keys if they were changed
        if (credentialForm.client_key) {
          updateData.client_key = credentialForm.client_key;
        }
        if (credentialForm.secret_key) {
          updateData.secret_key = credentialForm.secret_key;
        }

        await KauflandApi.updateKauflandCredential(
          editingCredential.id,
          updateData,
        );
        setSuccess("Credential updated successfully!");
      } else {
        // Create new credential
        await KauflandApi.createKauflandCredential(credentialForm);
        setSuccess("Credential created successfully!");
      }

      await loadCredentials(true); // Reload credentials
      setCredentialForm({
        display_name: "",
        client_key: "",
        secret_key: "",
      });
      setEditingCredential(null); // Clear editing state
      setCredentialDialog(false);
      setFormTestStatus(""); // Clear test status
    } catch (error) {
      setError(
        `Failed to ${editingCredential ? "update" : "create"} credential: ` +
          error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const testCredentialsFromForm = async () => {
    if (!credentialForm.client_key || !credentialForm.secret_key) {
      setFormTestStatus("❌ Please fill in Client Key and Secret Key first");
      return;
    }

    // Validate key lengths
    if (credentialForm.client_key.length !== 32) {
      setFormTestStatus("❌ Client Key must be exactly 32 characters");
      return;
    }

    if (credentialForm.secret_key.length !== 64) {
      setFormTestStatus("❌ Secret Key must be exactly 64 characters");
      return;
    }

    try {
      setLoading(true);
      setFormTestStatus("Testing...");
      const response = await KauflandApi.test_credentials_direct({
        client_key: credentialForm.client_key,
        secret_key: credentialForm.secret_key,
      });

      if (response.data.success) {
        setFormTestStatus("✅ Authentication successful!");
      } else {
        setFormTestStatus(
          "❌ Authentication failed: " +
            (response.data.error || "Unknown error"),
        );
      }
    } catch (error) {
      setFormTestStatus("❌ Authentication failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const testCredentials = async (credential) => {
    try {
      setLoading(true);
      const response = await KauflandApi.testKauflandCredentials(credential.id);

      if (response.data.success) {
        setSuccess("✅ Authentication successful!");
        await loadCredentials(true); // Reload to get updated status
      } else {
        setError(
          "❌ Authentication failed: " +
            (response.data.error || "Unknown error"),
        );
      }
    } catch (error) {
      setError("❌ Authentication failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCredential = (credential) => {
    setEditingCredential(credential);
    setCredentialForm({
      display_name: credential.display_name,
      client_key: "", // Don't populate masked keys
      secret_key: "", // Don't populate masked keys
    });
    setFormTestStatus(""); // Clear test status
    setCredentialDialog(true);
  };

  const deleteCredential = async (credentialId) => {
    try {
      setLoading(true);

      // Clear active credential if it's the one being deleted
      if (activeCredential?.id === credentialId) {
        setActiveCredential(null);
        localStorage.removeItem("activeKauflandCredential");
      }

      await KauflandApi.deleteKauflandCredential(credentialId);
      await loadCredentials(true);
      setSuccess("Credential deleted successfully!");
    } catch (error) {
      setError("Failed to delete credential: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={cp.outerCard}>
            <CardContent sx={{ p: 4 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                sx={{
                  ...cp.header,
                  p: 4,
                  borderRadius: 2,
                  color: "white",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    marginRight: "auto",
                    paddingRight: 2,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                    🛒 Kaufland API Credentials
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Manage your Kaufland API access credentials
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadCredentials}
                    disabled={loading}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCredentialDialog(true)}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Add Credentials
                  </Button>
                </Box>
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 3 }}
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              )}

              {success && (
                <Alert
                  severity="success"
                  sx={{ mb: 3 }}
                  onClose={() => setSuccess(null)}
                >
                  {success}
                </Alert>
              )}

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>How to Get Your Kaufland Credentials:</strong>
                </Typography>
                <ol style={{ margin: 0, paddingLeft: "20px" }}>
                  <li>Register as a seller on Kaufland marketplace</li>
                  <li>
                    Navigate to Settings → API Access in your seller account
                  </li>
                  <li>Generate API credentials (Client Key and Secret Key)</li>
                  <li>Client Key must be exactly 32 characters</li>
                  <li>Secret Key must be exactly 64 characters</li>
                  <li>Enter the credentials in the form below</li>
                </ol>
                <Typography variant="body2" mt={1}>
                  For more information, visit{" "}
                  <a
                    href="https://sellerportal.kaufland.de/settings/api"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#1976d2",
                      textDecoration: "underline",
                      fontWeight: "bold",
                    }}
                  >
                    Kaufland Seller API Documentation
                  </a>
                </Typography>
              </Alert>

              {credentials.length === 0 ? (
                <Box sx={cp.emptyState}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: "text.secondary" }}
                  >
                    No Credentials Found
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 3, color: "text.secondary" }}
                  >
                    Add your first Kaufland credential to get started
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCredentialDialog(true)}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      ...cp.ctaButton,
                    }}
                  >
                    Add Your First Credential
                  </Button>
                </Box>
              ) : (
                <List
                  sx={{
                    width: "100%",
                    border: "2px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    boxShadow: 2,
                  }}
                >
                  {credentials.map((credential, index) => (
                    <ListItem
                      key={credential.id}
                      divider={index < credentials.length - 1}
                      sx={{
                        px: 4,
                        py: 3,
                        borderBottom:
                          index < credentials.length - 1 ? "2px solid" : "none",
                        borderColor: "divider",
                        "&:hover": {
                          backgroundColor: "action.hover",
                          transform: "translateX(4px)",
                        },
                        transition: "all 0.2s ease",
                        borderRadius:
                          index === 0
                            ? "8px 8px 0 0"
                            : index === credentials.length - 1
                              ? "0 0 8px 8px"
                              : 0,
                      }}
                    >
                      <Grid container alignItems="center" spacing={3}>
                        <Grid item xs={12} md={8}>
                          <Typography variant="h6">
                            {credential.display_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: "break-all",
                              pr: 2,
                            }}
                          >
                            Client Key: {credential.client_key}
                          </Typography>
                          <Box
                            sx={{
                              mt: 0.5,
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Chip
                              label={credential.status || "Unknown"}
                              size="small"
                              color={
                                credential.status === "Connected"
                                  ? "success"
                                  : "error"
                              }
                            />
                            {/* Active Status Chip */}
                            {activeCredential?.id === credential.id && (
                              <Chip
                                label="ACTIVE"
                                size="small"
                                color="primary"
                                variant="filled"
                              />
                            )}
                          </Box>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                          sx={{
                            display: "flex",
                            justifyContent: { xs: "center", md: "flex-end" },
                            alignItems: "center",
                            gap: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          {/* Active Toggle Switch */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              mr: 2,
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    activeCredential?.id === credential.id
                                  }
                                  onChange={() =>
                                    toggleActiveCredential(credential)
                                  }
                                  disabled={credential.status !== "Connected"}
                                  size="small"
                                  sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                      color: "success.main",
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                      {
                                        backgroundColor: "success.main",
                                      },
                                  }}
                                />
                              }
                              label=""
                              sx={{ m: 0 }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color:
                                  activeCredential?.id === credential.id
                                    ? "success.main"
                                    : "text.secondary",
                                fontWeight: "bold",
                                mt: 0.5,
                              }}
                            >
                              {activeCredential?.id === credential.id
                                ? "Active"
                                : "Inactive"}
                            </Typography>
                          </Box>

                          <IconButton
                            onClick={() => testCredentials(credential)}
                            disabled={loading}
                            color="info"
                            title="Test Credentials"
                            sx={{
                              backgroundColor: "info.main",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "info.dark",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            {loading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <CheckIcon />
                            )}
                          </IconButton>
                          <IconButton
                            title="Edit Credential"
                            onClick={() => handleEditCredential(credential)}
                            color="warning"
                            sx={{
                              backgroundColor: "warning.main",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "warning.dark",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => deleteCredential(credential.id)}
                            title="Delete Credential"
                            sx={{
                              backgroundColor: "error.main",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "error.dark",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={credentialDialog}
        onClose={() => {
          setCredentialDialog(false);
          setEditingCredential(null);
          setFormTestStatus("");
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
            ...cp.dialogPaper,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            borderBottom: "2px solid",
            borderColor: "divider",
            ...cp.dialogHeader,
            color: "white",
            fontWeight: 600,
            fontSize: "1.25rem",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {editingCredential ? <EditIcon /> : <AddIcon />}
            </Box>
            <Typography variant="h6" component="div" fontWeight={600}>
              {editingCredential
                ? "Edit Kaufland API Credentials"
                : "Add Kaufland API Credentials"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, px: 4 }}>
          <Alert severity="info" sx={{ mb: 3, mx: 0 }}>
            <Typography variant="body2" fontWeight={500}>
              {editingCredential
                ? "Required: Display Name. Keys are optional - only fill to update them."
                : "Required: Client Key (32 chars) and Secret Key (64 chars)"}
            </Typography>
          </Alert>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                placeholder="e.g., Kaufland Production API"
                value={credentialForm.display_name}
                onChange={(e) =>
                  setCredentialForm((prev) => ({
                    ...prev,
                    display_name: e.target.value,
                  }))
                }
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Client Key"
                placeholder={
                  editingCredential
                    ? "Leave empty to keep existing key"
                    : "Enter your 32-character client key"
                }
                value={credentialForm.client_key}
                onChange={(e) =>
                  setCredentialForm((prev) => ({
                    ...prev,
                    client_key: e.target.value,
                  }))
                }
                required={!editingCredential}
                helperText={
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>
                      {editingCredential
                        ? "Optional - only fill to update"
                        : "Must be exactly 32 characters"}
                    </span>
                    <Chip
                      label={`${credentialForm.client_key.length}/32`}
                      size="small"
                      color={
                        credentialForm.client_key.length === 32
                          ? "success"
                          : credentialForm.client_key.length > 0
                            ? "warning"
                            : "default"
                      }
                      sx={{ height: 20, fontSize: "0.75rem" }}
                    />
                  </Box>
                }
                error={
                  credentialForm.client_key.length > 0 &&
                  credentialForm.client_key.length !== 32
                }
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontFamily: "monospace",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Secret Key"
                placeholder={
                  editingCredential
                    ? "Leave empty to keep existing key"
                    : "Enter your 64-character secret key"
                }
                type="password"
                value={credentialForm.secret_key}
                onChange={(e) =>
                  setCredentialForm((prev) => ({
                    ...prev,
                    secret_key: e.target.value,
                  }))
                }
                required={!editingCredential}
                helperText={
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>
                      {editingCredential
                        ? "Optional - only fill to update"
                        : "Must be exactly 64 characters"}
                    </span>
                    <Chip
                      label={`${credentialForm.secret_key.length}/64`}
                      size="small"
                      color={
                        credentialForm.secret_key.length === 64
                          ? "success"
                          : credentialForm.secret_key.length > 0
                            ? "warning"
                            : "default"
                      }
                      sx={{ height: 20, fontSize: "0.75rem" }}
                    />
                  </Box>
                }
                error={
                  credentialForm.secret_key.length > 0 &&
                  credentialForm.secret_key.length !== 64
                }
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontFamily: "monospace",
                  },
                }}
              />
            </Grid>
            {/* Add test status display */}
            {formTestStatus && (
              <Grid item xs={12}>
                <Alert
                  severity={
                    formTestStatus.includes("✅")
                      ? "success"
                      : formTestStatus.includes("Testing")
                        ? "info"
                        : "error"
                  }
                  sx={{
                    borderRadius: 2,
                    fontWeight: 500,
                  }}
                  icon={
                    formTestStatus.includes("Testing") ? (
                      <CircularProgress size={20} />
                    ) : undefined
                  }
                >
                  {formTestStatus}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: "2px solid",
            borderColor: "divider",
            gap: 1,
            justifyContent: "space-between",
            backgroundColor: "grey.50",
            borderRadius: "0 0 12px 12px",
          }}
        >
          <Button
            onClick={() => {
              setCredentialDialog(false);
              setEditingCredential(null);
              setFormTestStatus("");
            }}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={testCredentialsFromForm}
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
            disabled={
              loading ||
              !credentialForm.client_key ||
              !credentialForm.secret_key ||
              credentialForm.client_key.length !== 32 ||
              credentialForm.secret_key.length !== 64
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              minWidth: 140,
            }}
          >
            Test Credentials
          </Button>
          <Button
            onClick={handleCredentialSubmit}
            variant="contained"
            disabled={
              !credentialForm.display_name ||
              // For new credentials, require keys
              (!editingCredential &&
                (!credentialForm.client_key || !credentialForm.secret_key)) ||
              // Validate key lengths if provided
              (credentialForm.client_key &&
                credentialForm.client_key.length !== 32) ||
              (credentialForm.secret_key &&
                credentialForm.secret_key.length !== 64)
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              minWidth: 100,
              ...cp.ctaButton,
              "&:hover": {
                background: "linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)",
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: "grey.400",
                color: "white",
              },
              transition: "all 0.3s ease",
            }}
          >
            {editingCredential ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KauflandCredentials;
